<template>
    <div class="view-inner">
        <!-- Page Header -->
        <div class="page-header">
            <div>
                <h1 class="page-header-title"><i class="bi bi-shield-check"></i> Auditoría</h1>
                <p class="page-header-sub">Quién hizo qué y cuándo: cambios en los datos, inicios de sesión y descargas.</p>
            </div>
        </div>

        <!-- Filtros (se aplican en la base al presionar Buscar) -->
        <form class="filter-card" @submit.prevent="buscar()">
            <div class="row g-2">
                <div class="col-md-2">
                    <label class="form-label">Desde</label>
                    <input type="date" class="form-control form-control-sm" v-model="filtros.desde" :max="filtros.hasta || hoy">
                </div>
                <div class="col-md-2">
                    <label class="form-label">Hasta</label>
                    <input type="date" class="form-control form-control-sm" v-model="filtros.hasta" :min="filtros.desde" :max="hoy">
                </div>
                <div class="col-md-3">
                    <label class="form-label">Usuario</label>
                    <select class="form-select form-select-sm" v-model="filtros.user_id">
                        <option value="">Todos</option>
                        <option v-for="u in opciones.usuarios" :key="u.id" :value="u.id">{{ u.nombre || u.email }}</option>
                    </select>
                </div>
                <div class="col-md-2">
                    <label class="form-label">Acción</label>
                    <select class="form-select form-select-sm" v-model="filtros.accion">
                        <option value="">Todas</option>
                        <option v-for="a in opciones.acciones" :key="a" :value="a">{{ acciones[a] ? acciones[a].texto : a }}</option>
                    </select>
                </div>
                <div class="col-md-3">
                    <label class="form-label">Módulo</label>
                    <select class="form-select form-select-sm" v-model="filtros.modulo">
                        <option value="">Todos</option>
                        <option v-for="m in opciones.modulos" :key="m" :value="m">{{ m }}</option>
                    </select>
                </div>
                <div class="col-md-5">
                    <label class="form-label">Buscar</label>
                    <input type="text" class="form-control form-control-sm" v-model="filtros.buscar"
                        placeholder="Rastreo, cliente, placa, email o nombre de quien lo hizo">
                </div>
                <div class="col-12 col-md-auto ms-auto d-flex justify-content-end align-items-end">
                    <div class="filter-actions">
                        <button type="submit" class="btn-filter"><i class="bi bi-search"></i> Buscar</button>
                        <button type="button" class="btn-clear" @click="limpiarFiltros()"><i class="bi bi-x-circle"></i> Limpiar</button>
                    </div>
                </div>
            </div>
        </form>

        <!-- Registros (estándar: ESTANDAR_LISTADOS.md) -->
        <div>
            <div class="table-responsive">
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th>Fecha y hora</th>
                            <th>Usuario</th>
                            <th>Acción</th>
                            <th>Módulo</th>
                            <th>Registro</th>
                            <th>IP</th>
                            <th class="col-acciones">Detalle</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="r in registros" :key="r.id">
                            <td class="celda-id">{{ formatoFechaHora(r.created_at) }}</td>
                            <td class="celda-entidad">
                                <span class="entidad-nombre">{{ r.usuario || 'Sin sesión' }}</span>
                            </td>
                            <td>
                                <span class="au-accion" :class="'au-' + r.accion">
                                    <i :class="(acciones[r.accion] || {}).icono"></i>
                                    {{ acciones[r.accion] ? acciones[r.accion].texto : r.accion }}
                                </span>
                            </td>
                            <td>{{ r.modulo }}</td>
                            <td class="au-referencia" :title="r.referencia">{{ r.referencia || '—' }}</td>
                            <td class="celda-id au-ip">{{ r.ip || '—' }}</td>
                            <td class="col-acciones">
                                <button class="btn-icon btn-icon-view" @click="verDetalle(r)" title="Ver detalle" aria-label="Ver detalle">
                                    <i class="bi bi-eye"></i>
                                </button>
                            </td>
                        </tr>
                        <tr v-if="isLoading && !registros.length" class="empty-row">
                            <td colspan="7"><span class="spinner-border spinner-border-sm me-2"></span>Cargando…</td>
                        </tr>
                        <tr v-else-if="!registros.length" class="empty-row">
                            <td colspan="7">No hay registros con estos filtros</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <!-- Paginado en la base de datos (25 por página) -->
            <PaginacionListado :pagina="pagina" :total-paginas="totalPaginas" :total="totalRegistros"
                :por-pagina="porPagina" :en-pagina="registros.length" :cargando="isLoading"
                etiqueta="registros" @cambiar="cambiarPagina" />
        </div>

        <!-- Detalle de un registro -->
        <div v-if="detalle" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);"
            @keydown.esc="detalle = null" @click.self="detalle = null">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            {{ acciones[detalle.accion] ? acciones[detalle.accion].texto : detalle.accion }} · {{ detalle.modulo }}
                        </h5>
                        <button type="button" class="btn-close" @click="detalle = null" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">
                        <dl class="au-resumen">
                            <div><dt>Fecha y hora</dt><dd>{{ formatoFechaHora(detalle.created_at, true) }}</dd></div>
                            <div><dt>Usuario</dt><dd>{{ detalle.usuario || 'Sin sesión' }}</dd></div>
                            <div><dt>Registro</dt><dd>{{ detalle.referencia || '—' }}<small v-if="detalle.entidad_id"> (id {{ detalle.entidad_id }})</small></dd></div>
                            <div><dt>Origen</dt><dd>{{ detalle.ip || '—' }}<small v-if="detalle.ruta"> · {{ detalle.ruta }}</small></dd></div>
                        </dl>

                        <template v-if="filasCambios.length">
                            <h6 class="au-subtitulo">
                                {{ detalle.accion === 'editar' ? 'Qué cambió' : detalle.accion === 'crear' ? 'Datos registrados' : 'Datos que tenía' }}
                            </h6>
                            <div class="table-responsive">
                                <table class="table au-cambios">
                                    <thead>
                                        <tr>
                                            <th>Campo</th>
                                            <th v-if="detalle.accion === 'editar'">Antes</th>
                                            <th>{{ detalle.accion === 'editar' ? 'Después' : 'Valor' }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="fila in filasCambios" :key="fila.campo">
                                            <td class="au-campo">{{ fila.etiqueta }}</td>
                                            <td v-if="detalle.accion === 'editar'" class="au-antes">{{ mostrarValor(fila.antes) }}</td>
                                            <td :class="{ 'au-despues': detalle.accion === 'editar' }">
                                                {{ mostrarValor(detalle.accion === 'eliminar' ? fila.antes : fila.despues) }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </template>
                        <p v-else class="au-sin-cambios">Este registro no tiene detalle de campos.</p>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-secondary" @click="detalle = null">Cerrar</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'
import PaginacionListado from '@/components/PaginacionListado.vue'
import paginacionServidor from '@/mixins/paginacionServidor'

// Nombre, ícono y color de cada acción
const ACCIONES = {
    crear: { texto: 'Creó', icono: 'bi bi-plus-circle' },
    editar: { texto: 'Editó', icono: 'bi bi-pencil' },
    eliminar: { texto: 'Eliminó', icono: 'bi bi-trash' },
    login: { texto: 'Inició sesión', icono: 'bi bi-box-arrow-in-right' },
    login_fallido: { texto: 'Intento fallido', icono: 'bi bi-exclamation-triangle' },
    logout: { texto: 'Cerró sesión', icono: 'bi bi-box-arrow-right' },
    descarga: { texto: 'Descargó', icono: 'bi bi-download' },
}

// Nombres legibles de campos frecuentes; el resto se arma a partir del nombre de la columna
const CAMPOS = {
    estado_entrega: 'Estado',
    id_estados_envio: 'Estado',
    id_vehiculo: 'Vehículo (id)',
    id_chofer: 'Chofer (id)',
    id_auxiliar: 'Auxiliar (id)',
    sucursal_remitente_id: 'Sucursal remitente (id)',
    sucursal_destinatario_id: 'Sucursal destino (id)',
    empresa_id: 'Empresa (id)',
    tipo_unidad_id: 'Tipo de unidad (id)',
    nro_pedido: 'N° de pedido',
    numero_factura: 'N° de factura',
    guia_remitente: 'Guía de remisión',
    guia_transportista: 'Guía transportista',
    orden_compra: 'Orden de compra',
    ruc: 'RUC',
    dni: 'DNI',
    otm: 'OTM',
    ubigeo: 'Ubigeo',
}

const filtrosVacios = () => ({ desde: '', hasta: '', user_id: '', accion: '', modulo: '', buscar: '' })

export default {
    name: 'AuditoriaPage',
    components: { PaginacionListado },
    mixins: [paginacionServidor],
    data() {
        return {
            endpointListado: '/auditoria',
            isLoading: false,
            filtros: filtrosVacios(),
            opciones: { usuarios: [], acciones: Object.keys(ACCIONES), modulos: [] },
            acciones: ACCIONES,
            detalle: null,
            hoy: new Date().toLocaleDateString('en-CA'), // YYYY-MM-DD local
        }
    },
    computed: {
        filasCambios() {
            const cambios = (this.detalle && this.detalle.cambios) || {}
            return Object.keys(cambios).map(campo => ({
                campo,
                etiqueta: CAMPOS[campo] || this.etiquetaCampo(campo),
                antes: cambios[campo][0],
                despues: cambios[campo][1],
            }))
        },
    },
    mounted() {
        this.cargarListado()
        this.cargarOpciones()
    },
    methods: {
        // Filtros que se envían al API (el filtrado y el paginado se hacen en la base)
        parametrosFiltro() {
            const p = {}
            Object.keys(this.filtros).forEach(k => {
                const v = typeof this.filtros[k] === 'string' ? this.filtros[k].trim() : this.filtros[k]
                if (v !== '' && v !== null) p[k] = v
            })
            return p
        },
        async cargarOpciones() {
            try {
                const { data } = await axios.get('/auditoria/filtros')
                this.opciones = data
            } catch (error) {
                console.error('Error al cargar los filtros de auditoría:', error)
            }
        },
        buscar() {
            if (this.filtros.desde && this.filtros.hasta && this.filtros.hasta < this.filtros.desde) {
                Swal.fire({ icon: 'warning', title: 'Rango de fechas', text: 'La fecha "hasta" no puede ser anterior a la fecha "desde".' })
                return
            }
            this.aplicarFiltros()
        },
        limpiarFiltros() {
            this.filtros = filtrosVacios()
            this.aplicarFiltros()
        },
        verDetalle(registro) {
            this.detalle = registro
        },
        etiquetaCampo(campo) {
            const texto = campo.replace(/_/g, ' ').trim()
            return texto.charAt(0).toUpperCase() + texto.slice(1)
        },
        mostrarValor(valor) {
            if (valor === null || valor === undefined || valor === '') return '—'
            if (typeof valor === 'boolean') return valor ? 'Sí' : 'No'
            if (typeof valor === 'object') return JSON.stringify(valor)
            return String(valor)
        },
        formatoFechaHora(fecha, conSegundos = false) {
            if (!fecha) return ''
            return new Date(fecha).toLocaleString('es-PE', {
                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                ...(conSegundos ? { second: '2-digit' } : {}),
            })
        },
    },
}
</script>

<style scoped>
/* Acción: insignia con color por tipo */
.au-accion {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 0.72rem;
    font-weight: 600;
    white-space: nowrap;
}
.au-crear         { background: #e8f5e9; color: #2e7d32; }
.au-editar        { background: #e8eaf6; color: #3949ab; }
.au-eliminar      { background: #ffebee; color: #c62828; }
.au-login         { background: #e0f2f1; color: #00695c; }
.au-logout        { background: #f2f4f7; color: #566a7f; }
.au-login_fallido { background: #fff3e0; color: #e65100; }
.au-descarga      { background: #f3e5f5; color: #6a1b9a; }

.au-referencia {
    max-width: 320px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.au-ip { color: #8592a3; }

/* Detalle */
.au-resumen {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 24px;
    margin: 0 0 18px;
}
.au-resumen dt {
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #8592a3;
    margin-bottom: 2px;
}
.au-resumen dd { margin: 0; font-size: 0.84rem; color: #2b2f3a; overflow-wrap: anywhere; }
.au-resumen dd small { color: #8592a3; }
.au-subtitulo { font-size: 0.85rem; font-weight: 700; color: #2b2f3a; margin: 0 0 8px; }
.au-cambios { font-size: 0.8rem; margin: 0; }
.au-cambios td { overflow-wrap: anywhere; vertical-align: top; }
.au-campo { font-weight: 600; color: #566a7f; width: 30%; }
.au-antes { color: #c62828; background: #fff7f7; }
.au-despues { color: #2e7d32; background: #f5fbf5; }
.au-sin-cambios { font-size: 0.82rem; color: #8592a3; margin: 0; }

@media (max-width: 576px) {
    .au-resumen { grid-template-columns: 1fr; }
}
</style>
